import { useState, type FormEvent } from 'react';
import { useActionData, useLoaderData, useNavigation, useSubmit } from 'react-router-dom';
import { Button, FormField, Input } from '@dma/ui';
import { ImageUploadField } from '../../media';
import type { BannerDto } from '../application/dto/BannerDto';
import type { SaveBannerDto } from '../application/dto/SaveBannerDto';
import './AdminBannerPage.css';

export interface AdminBannerLoaderData {
  banner: BannerDto | null;
}

interface ActionFailure {
  ok: false;
  message: string;
}

/**
 * A settings page, not a list+form pair like the other admin CRUD screens
 * (news, scientific-content) — there is only one banner, so create and edit
 * are the same form.
 */
export function AdminBannerPage() {
  const { banner } = useLoaderData() as AdminBannerLoaderData;
  const actionData = useActionData() as ActionFailure | undefined;
  const navigation = useNavigation();
  const submit = useSubmit();
  const isSubmitting = navigation.state === 'submitting';

  const [imageUrl, setImageUrl] = useState(banner?.imageUrl ?? '');
  const [altText, setAltText] = useState(banner?.altText ?? '');
  const [isActive, setIsActive] = useState(banner?.isActive ?? false);

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    const payload: SaveBannerDto = {
      imageUrl: imageUrl.trim() === '' ? null : imageUrl,
      altText,
      isActive
    };
    submit(payload as unknown as Record<string, unknown>, { method: 'post', encType: 'application/json' });
  }

  return (
    <div className="dma-admin-banner">
      <h1>بنر صفحه اصلی</h1>
      <p className="dma-admin-banner__hint">
        این تصویر درست زیر هدر، در بالای صفحه اصلی سایت نمایش داده می‌شود. عرض آن برابر عرض کامل
        صفحه و ارتفاعش حدود یک‌سوم صفحه است.
      </p>

      <form onSubmit={handleSubmit} noValidate>
        {actionData && actionData.ok === false ? (
          <p className="dma-admin-banner__error" role="alert">
            {actionData.message}
          </p>
        ) : null}

        <ImageUploadField
          label="تصویر بنر"
          fieldId="bannerImageUrl"
          value={imageUrl}
          onChange={setImageUrl}
          folder="banner"
          disabled={isSubmitting}
        />

        <FormField label="متن جایگزین تصویر (برای دسترسی‌پذیری)" htmlFor="bannerAltText">
          <Input
            id="bannerAltText"
            value={altText}
            onChange={(e) => setAltText(e.target.value)}
            placeholder="مثلاً: نمایی از معدن کلسیت سفید تک‌اشخانه"
            disabled={isSubmitting}
          />
        </FormField>

        <label className="dma-admin-banner__toggle">
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
            disabled={isSubmitting}
          />
          بنر روی سایت نمایش داده شود
        </label>

        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'در حال ذخیره...' : 'ذخیره'}
        </Button>
      </form>
    </div>
  );
}
